'use client';

import { useAtomValue } from 'jotai';
import Link from 'next/link';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import Button from '@gds/Button/Button';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import type { PromotionCta } from './Promotions.entity';
import { generatePromotionsHomePageTracking } from './PromotionsHomePage.tracking';

import styles from './PromotionsHomePage.module.css';

interface PromotionHomePageCtaProps {
  primaryCta?: PromotionCta | null;
  secondaryCta?: PromotionCta | null;
  index: number;
}

export const PromotionsHomePageCta = ({
  primaryCta,
  secondaryCta,
  index,
}: PromotionHomePageCtaProps) => {
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const handleClick = (ctaWording: string, ctaUrl: string) => {
    const { event, context } = generatePromotionsHomePageTracking({
      searchParams,
      userId,
      formState,
      ctaType: 'button',
      ctaWording,
      ctaUrl,
    });

    trackCustomEvent({ event, context });
  };

  return (
    <>
      {secondaryCta && (
        <Button
          Component={Link}
          href={secondaryCta.link}
          className={styles.secondaryCta}
          variant="link"
          dataTestId={`secondaryCta${index}`}
          onClick={() => handleClick(secondaryCta.label, secondaryCta.link)}
        >
          {secondaryCta.label}
        </Button>
      )}
      {primaryCta && (
        <Button
          Component={Link}
          href={primaryCta.link}
          className={styles.primaryCta}
          color="primary"
          aria-label="promo link"
          variant={index === 0 ? 'contained' : 'outlined'}
          dataTestId={`primaryCta${index}`}
          onClick={() => handleClick(primaryCta.label, primaryCta.link)}
        >
          {primaryCta.label}
        </Button>
      )}
    </>
  );
};
